import { createConsumer, createSubscriber, IConsumer, IEvent } from '@cian/events-log';

import { TThunkAction } from '../../types/redux';
import { IOpenAuthModalViewData } from '../../types/microfrontends';
import { getAuthenticateUrl } from '../../utils/getAuthenticateUrl';
import { getUser, setUserData } from '../user';

export interface IRequestAuthenticationParameters {
  viewData?: IOpenAuthModalViewData;
  onSuccess?(): void;
  onCloseNoAuth?(): void;
}

const TOPIC_USER = 'user';

type TConsumerSingleton = (() => IConsumer) & { _instance?: IConsumer };

const getUserConsumer: TConsumerSingleton = (): IConsumer => {
  if (!getUserConsumer._instance) {
    getUserConsumer._instance = createConsumer({ topic: TOPIC_USER });
  }

  return getUserConsumer._instance;
};

/**
 * Открывает модалку авторизации.
 *
 * На вебе это делает микрофронтенд шапки через свой API-рантайм
 * (`/v3/open-auth-modal/`): легаси-событие `authenticate` шапка больше
 * не слушает.
 *
 * В вебвью шапки нет — там модалку рисует authentication-service-frontend,
 * который по-прежнему подписан на легаси-событие `authenticate`.
 *
 * Результат авторизации в обоих случаях приходит событиями `authenticated` /
 * `authentication_modal_closed` в топике `user`.
 *
 * Если открыть модалку нечем (нет API микрофронтенда шапки или вызов упал) —
 * отправляем пользователя на страницу авторизации, чтобы он не остался
 * без возможности авторизоваться.
 */
export function requestAuthentication(parameters?: IRequestAuthenticationParameters): TThunkAction {
  return (dispatch, _, context) => {
    if (typeof globalThis.window === 'undefined') {
      return;
    }

    const { microfrontendsAPI, logger } = context;

    const redirectToAuthenticatePage = () => {
      window.location.assign(getAuthenticateUrl(window.location.href));
    };

    if (!microfrontendsAPI) {
      redirectToAuthenticatePage();

      return;
    }

    const consumer = getUserConsumer();

    const userSubscriber = createSubscriber(async (event: IEvent<unknown>) => {
      if (event.type === 'authenticated') {
        dispatch(setUserData({ isAuthenticated: true }));

        await dispatch(getUser());

        await microfrontendsAPI.header.updateHeader();

        if (parameters && parameters.onSuccess) {
          parameters.onSuccess();
        }

        consumer.unsubscribe(userSubscriber);

        return;
      }

      if (event.type === 'authentication_modal_closed') {
        if (parameters && parameters.onCloseNoAuth) {
          parameters.onCloseNoAuth();
        }

        consumer.unsubscribe(userSubscriber);
      }
    });

    consumer.subscribe(userSubscriber);

    microfrontendsAPI.header
      .openAuthModal({
        autoRefresh: false,
        alert: null,
        useDefaultView: false,
        viewData: parameters?.viewData || null,
        source: null,
      })
      .catch(error => {
        logger.error(error, { message: 'Failed to open auth modal via header-frontend' });

        consumer.unsubscribe(userSubscriber);

        redirectToAuthenticatePage();
      });
  };
}
